import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { goClient, isRequestError } from '../../utils/exportClient';

export const WORK_QUEUE_POLL_MS = 5 * 60 * 1000;
const SEEN_PREFIX = 'work_queue_seen_v1';
const INITIAL_RETRY_MS = 20 * 1000;

// Where each queue key opens in dapur; the Go route owns the keys.
export const QUEUE_LINKS = {
  outstanding: '/sales/outstanding',
  outstanding_confirmation: '/sales/outstanding',
  quality_control: '/sales/quality_control',
  verification_process: '/sales/legit_check',
  verification_passed: '/sales/pre-delivering',
  follow_up_approval: '/sales/follow-up?activeTab=followUpApproval',
  need_refund: '/sales/need-refund',
  sell_requests: '/sell_request',
  pending_offers: '/offers',
  seller_verification: '/seller_verification'
};
const SALE_KEYS = ['outstanding', 'outstanding_confirmation', 'quality_control', 'verification_process', 'verification_passed', 'follow_up_approval', 'need_refund'];

export const fetchWorkQueues = () =>
  goClient().get('/admins/dashboard/work-queue').then(res => (res.status === 202 || !res.data ? null : res.data.data));

const seenKey = user => `${SEEN_PREFIX}:${user || 'anon'}`;
const loadSeen = user => { try { return JSON.parse(localStorage.getItem(seenKey(user))) || {}; } catch (e) { return {}; } };
const saveSeen = (user, seen) => { try { localStorage.setItem(seenKey(user), JSON.stringify(seen)); } catch (e) { /* storage blocked */ } };

export const grownSince = (queues, seen) => queues.filter(q => q.count > (seen[q.key] || 0));

class WorkQueueBell extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, queues: null, seen: loadSeen(props.userKey), failed: false, refreshedAt: null };
    this.generation = 0;
    this.toggle = this.toggle.bind(this);
    this.refresh = this.refresh.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
  }

  componentDidMount() {
    this.refresh();
    this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, this.props.pollMs || WORK_QUEUE_POLL_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userKey !== this.props.userKey) this.setState({ seen: loadSeen(this.props.userKey) });
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearInterval(this.timer);
    clearTimeout(this.retry);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    if (!document.hidden && this.state.refreshedAt && Date.now() - this.state.refreshedAt > (this.props.pollMs || WORK_QUEUE_POLL_MS)) this.refresh();
  }

  refresh() {
    const generation = ++this.generation;
    (this.props.fetchQueues || fetchWorkQueues)()
      .then(data => {
        if (this.unmounted || generation !== this.generation) return;
        if (!data) {
          if (!this.state.queues) this.retry = setTimeout(this.refresh, this.props.retryMs || INITIAL_RETRY_MS);
          return;
        }
        this.setState({ queues: data.queues || [], failed: false, refreshedAt: Date.now() });
      })
      .catch(err => {
        if (!this.unmounted && generation === this.generation) this.setState({ failed: true });
        if (!isRequestError(err)) throw err;
      });
  }

  toggle() {
    const open = !this.state.open;
    if (!open && this.state.queues) {
      const seen = {};
      this.state.queues.forEach(q => { seen[q.key] = q.count; });
      saveSeen(this.props.userKey, seen);
      this.setState({ open, seen });
      return;
    }
    this.setState({ open });
  }

  renderRow(q, grown) {
    const isNew = grown.some(g => g.key === q.key);
    const delta = isNew ? q.count - (this.state.seen[q.key] || 0) : 0;
    return (
      <Link key={q.key} to={QUEUE_LINKS[q.key] || '/dashboard'} className="dropdown-item d-flex justify-content-between align-items-center" onClick={this.toggle} style={{ whiteSpace: 'normal' }}>
        <span style={{ color: q.count ? undefined : '#73818f' }}>{q.label}</span>
        <strong style={{ marginLeft: 12, color: isNew ? '#f86c6b' : (q.count ? undefined : '#73818f') }}>
          {q.count}{delta > 0 && <small style={{ marginLeft: 4 }}>+{delta}</small>}
        </strong>
      </Link>
    );
  }

  render() {
    const { open, queues, seen, failed, refreshedAt } = this.state;
    const grown = queues ? grownSince(queues, seen) : [];
    const sales = queues ? queues.filter(q => SALE_KEYS.indexOf(q.key) !== -1) : [];
    const others = queues ? queues.filter(q => SALE_KEYS.indexOf(q.key) === -1) : [];
    const age = refreshedAt ? Math.round((Date.now() - refreshedAt) / 60000) : null;
    return (
      <Dropdown nav isOpen={open} toggle={this.toggle} className="nav-item d-md-down-none work-queue-bell">
        <DropdownToggle nav aria-label={grown.length ? `Work queue, ${grown.length} new` : 'Work queue'} style={{ position: 'relative' }}>
          <i className="icon-bell"></i>
          {grown.length > 0 && <span className="badge badge-pill badge-danger">{grown.length}</span>}
        </DropdownToggle>
        <DropdownMenu right style={{ minWidth: 320, padding: 0 }}>
          <div className="dropdown-header d-flex justify-content-between">
            <span>Work queue</span>
            <small className="text-muted">{failed ? 'could not refresh' : (queues ? (age ? `updated ${age} min ago` : 'just updated') : 'loading…')}</small>
          </div>
          {sales.length > 0 && <div className="dropdown-header">Sales</div>}
          {sales.map(q => this.renderRow(q, grown))}
          {others.length > 0 && <div className="dropdown-header">Listings and sellers</div>}
          {others.map(q => this.renderRow(q, grown))}
        </DropdownMenu>
      </Dropdown>
    );
  }
}

export default WorkQueueBell;
